import {
  Text,
  Checkbox,
  CustomFieldsEditor,
  DatePickerField,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { isPositiveInteger, required, skip } from '@sequencehq/utils/validation'
import { CustomerAliasSelect, CustomerSelect } from 'Customer/index'
import { useNotifications } from '@sequencehq/core-components'
import { useState, useCallback, useMemo } from 'react'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'

/**
 * Helper function to create customer/alias validation configuration
 * @param withAlias Whether to use customer alias validation or customer ID validation
 * @returns Array of validation configurations for customerAlias and customerId fields
 */
const createCustomerAliasValidation = (withAlias: boolean) => [
  {
    property: 'customerAlias' as const,
    validation: [withAlias ? required : skip]
  },
  { property: 'customerId' as const, validation: [withAlias ? skip : required] }
]
import { format, HOURS } from '@sequencehq/utils/dates'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useMutation, useQuery } from '@sequencehq/api'
import { Primary14SemiBold } from '@sequencehq/core-components'
import { GreyGrey80 } from '@sequencehq/core-components'
import { Primary13SemiBold } from '@sequencehq/core-components'
import { UsageEventTypeSelect } from '../../components/UsageEventTypeSelect'

type CustomerAliasFields = {
  customerAlias: string
  customerId: string
}

type FormValues = CustomerAliasFields & {
  eventType: string
  eventTimestamp: string
  total: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const CreateUsageEventDrawer = ({ isOpen, onClose }: Props) => {
  const { displayNotification } = useNotifications()
  const aliasesInfiniteQuery = useLoadAllItemsQuery(
    dashboard20240730Client.getCustomerAliases,
    { limit: 100 },
    { staleTime: 1 * HOURS }
  )

  const aliases = useMemo(
    () => aliasesInfiniteQuery.data?.pages.flatMap(page => page.items),
    [aliasesInfiniteQuery.data]
  )
  const { data: usageEventPropertyData } = useQuery(
    dashboard20240730Client.getUniqueUsageEventProperties
  )

  const usageEventPropertyOptions =
    usageEventPropertyData?.items.map(v => ({
      id: v,
      value: v,
      label: v
    })) ?? []

  const [eventPropertyFields, setEventPropertyFields] = useState<
    {
      id: string
      eventProperty: string
      value: string
    }[]
  >([
    {
      id: crypto.randomUUID(),
      eventProperty: '',
      value: ''
    }
  ])

  const { mutateAsync: createUsageEvent } = useMutation(
    dashboard20240730Client.postUsageEvent,
    {
      onSuccess: () => {
        displayNotification('Usage event added', { type: 'success' })
        onClose()
      },
      onError: () => {
        displayNotification('Failed to add usage event', { type: 'error' })
      }
    }
  )

  const [withAlias, setWithAlias] = useState(false)

  const form = useForm<FormValues>({
    value: {
      customerAlias: '',
      customerId: '',
      eventType: '',
      eventTimestamp: '',
      total: ''
    },
    fieldConfiguration: [
      ...createCustomerAliasValidation(withAlias),
      { property: 'eventType' as const, validation: [required] },
      { property: 'eventTimestamp' as const, validation: [required] },
      { property: 'total' as const, validation: [isPositiveInteger] }
    ]
  })

  const handleAliasToggle = useCallback(
    (checked: boolean) => {
      setWithAlias(checked)
      // Clear the field that's no longer being used
      if (checked) {
        form.fields.customerId.onChange('')
      } else {
        form.fields.customerAlias.onChange('')
      }
    },
    [form.fields.customerId, form.fields.customerAlias]
  )

  /**
   * Converts the form data to the expected customer alias format for the API.
   * When using aliases: converts alias ID (from CustomerAliasSelect) to alias value (expected by API)
   * When using customer ID: returns the customer ID directly
   * @returns The customer alias value or customer ID to send to the API
   */
  const getCustomerAlias = useCallback((): string => {
    if (withAlias) {
      const aliasId = form.queries.formData.customerAlias
      const aliasObject = aliases?.find(alias => alias.id === aliasId)
      return aliasObject?.value || aliasId // fallback to ID if not found
    } else {
      return form.queries.formData.customerId
    }
  }, [withAlias, form.queries.formData, aliases])

  const handleSubmit = async () => {
    // Validate required fields before submission
    if (!form.queries.formData.eventType.trim()) {
      displayNotification('Please select an event type', { type: 'error' })
      return false
    }

    if (!form.queries.formData.eventTimestamp.trim()) {
      displayNotification('Please select an event timestamp', { type: 'error' })
      return false
    }

    if (withAlias && !form.queries.formData.customerAlias.trim()) {
      displayNotification('Please select a customer alias', { type: 'error' })
      return false
    }

    if (!withAlias && !form.queries.formData.customerId.trim()) {
      displayNotification('Please select a customer', { type: 'error' })
      return false
    }

    const customerAlias = getCustomerAlias()

    const response = await createUsageEvent({
      customerAlias,
      eventType: form.queries.formData.eventType,
      eventTimestamp: `${format(
        form.queries.formData.eventTimestamp as unknown as Date,
        'yyyy-MM-dd'
      )}T00:00:00Z`,
      eventProperties: eventPropertyFields.reduce(
        (acc, { eventProperty, value }) => {
          if (!eventProperty && !value) {
            return acc
          }

          return { ...acc, [eventProperty]: value.trim() }
        },
        {} as Record<string, unknown>
      )
    })

    return !!response
  }

  const handleClose = () => {
    form.mutators.resetFormData()
    setEventPropertyFields([
      {
        id: crypto.randomUUID(),
        eventProperty: '',
        value: ''
      }
    ])
    onClose()
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      variant="drawer"
      title="New usage event"
      cancelButtonText="Cancel"
      submitButtonText="Add event"
      submittingButtonText="Adding usage event..."
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Flex direction="column" gap="8px" data-testid="usageEvents.new.form">
        <CustomerSelect
          disabled={withAlias}
          label="Customer"
          onChange={value => form.fields.customerId.onChange(value)}
          value={form.fields.customerId.value as string}
          allowCreate={false}
          validationErrors={form.fields.customerId.validationErrors}
        />
        <Flex mb="12px">
          <Checkbox
            label="Use customer alias instead"
            isChecked={withAlias}
            onChange={event => handleAliasToggle(event.target.checked)}
            data-testid="useCustomerAlias"
          />
        </Flex>
        {withAlias && (
          <CustomerAliasSelect
            data-testid="usageEvents.new.customerAlias"
            label="Customer alias"
            onChange={value => form.fields.customerAlias.onChange(value)}
            value={form.fields.customerAlias.value as string}
            validationErrors={form.fields.customerAlias.validationErrors}
          />
        )}
        <DatePickerField
          width="100%"
          label="Event timestamp"
          placeholder="Select date"
          value={
            form.fields.eventTimestamp.value
              ? new Date(form.fields.eventTimestamp.value)
              : undefined
          }
          onChange={value =>
            form.fields.eventTimestamp.onChange(
              value ? format(value, 'yyyy-MM-dd') : ''
            )
          }
        />
        <UsageEventTypeSelect
          value={form.fields.eventType.value}
          onChange={form.fields.eventType.onChange}
        />

        <Text {...Primary14SemiBold} marginTop="8px">
          Filter
        </Text>
        <Flex alignContent="center" alignItems="center" gap={2}>
          <Text
            width="50%"
            {...Primary13SemiBold}
            color={GreyGrey80}
            display="flex"
            gap={1}
          >
            Event property{' '}
          </Text>
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            Value
          </Text>
        </Flex>
        <CustomFieldsEditor<{
          id: string
          value: string
          eventProperty: string
        }>
          fields={eventPropertyFields}
          onAdd={() =>
            setEventPropertyFields([
              ...eventPropertyFields,
              {
                id: crypto.randomUUID(),
                eventProperty: '',
                value: ''
              }
            ])
          }
          onRemove={id =>
            setEventPropertyFields(eventPropertyFields.filter(f => f.id !== id))
          }
          addButtonLabel="Add event property"
          alwaysShowOneRow={false}
          data-testid="usageEvents.new.eventProperties"
          renderCells={(field, siblingFields) => (
            <CustomFieldsEditor.Row
              keyValue={field.eventProperty}
              onKeyChange={next =>
                setEventPropertyFields(prev =>
                  prev.map(f =>
                    f.id === field.id ? { ...f, eventProperty: next } : f
                  )
                )
              }
              siblingFields={siblingFields.map(f => ({
                ...f,
                key: f.eventProperty
              }))}
              keyOptions={usageEventPropertyOptions}
              keyPlaceholder="Select event property"
              keyCellSizing={{ width: '220px' }}
              value={field.value}
              onValueChange={next =>
                setEventPropertyFields(prev =>
                  prev.map(f => (f.id === field.id ? { ...f, value: next } : f))
                )
              }
            />
          )}
          addButtonVariant="ghost"
        />
      </Flex>
    </SimpleModal>
  )
}
